import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";

const BRAND_GREEN = "#33F7BE";

/* The dark-card tokens v0 sets on gradient product cards (DARK_CARD_VARS in
   components/beyond-turo/beyond-products.tsx), as the RGB channels our Tailwind colours read.
   The 14% white border is on the .tnt-dark-card class (tnt.css). */
export const DARK_CARD_VARS = {
  "--tw-c-foreground": "250 250 250",
  "--tw-c-muted-foreground": "183 183 183",
  "--tw-c-background": "13 13 13",
  "--tw-c-card": "13 13 13",
  "--tw-c-muted": "27 27 27",
};

/* Photo collage beside the rentAcademy copy: four square tiles that "liquid" in once the block
   scrolls into view. Photos are the WebP versions already shipped for /turo-alternative. */
const TILES = [
  { src: "/img/turo-alternative/academy/studio-duo.webp", alt: "Two operators recording a rental business podcast episode together" },
  { src: "/img/turo-alternative/academy/mic-smoke.webp", alt: "Studio microphone lit in warm and cool stage light" },
  { src: "/img/turo-alternative/academy/training-session.webp", alt: "Operator walking a teammate through fleet performance on a screen" },
  { src: "/img/turo-alternative/academy/expo-floor.webp", alt: "Busy industry conference floor filled with attendees" },
];

function RentAcademyCollage() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="grid w-full max-w-xs lg:w-[320px] xl:w-[360px] grid-cols-2 gap-2.5">
      {TILES.map((tile, index) => (
        <figure
          key={tile.src}
          className={`aspect-square overflow-hidden rounded-xl border border-white/15 bg-white/5 shadow-lg ${visible ? "liquid-in-x" : "opacity-0"}`}
          style={visible ? { animationDelay: `${index * 110}ms` } : undefined}
        >
          <img src={tile.src} alt={tile.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
        </figure>
      ))}
    </div>
  );
}

const PILLARS = [
  "Industry Talk — Expert discussions, industry insights, and best practices.",
  "PASE — Rentid product, feature, and service education.",
  "Thought Leadership — Expert training, mentorship, and firsthand experience.",
];

/* rentAcademy product card, ported from v0's RentAcademyBlock (a ProductShell with the props the
   /tnt-group page passes: gradient background, spacious padding, logo, no subcopy, no CTA). */
export function RentAcademyBlock({ logo, gradient, logoClassName = "h-16 sm:h-20" }) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 md:pt-12">
      <div
        className="tnt-dark-card relative overflow-hidden rounded-3xl p-6 sm:p-10 lg:p-14 shadow-lg"
        style={{ ...DARK_CARD_VARS, backgroundColor: "#050505", backgroundImage: gradient }}
      >
        {/* Identity — logo on the left */}
        <div className="flex flex-wrap items-center justify-between gap-6">
          <img src={logo} alt="RentAcademy logo" className={`${logoClassName} w-auto`} />
        </div>

        <div className="lg:flex lg:items-start lg:gap-10">
          {/* Left column — copy */}
          <div className="min-w-0 lg:flex-1">
            <h3 className="mt-8 sm:mt-10 lg:mt-12 font-heading text-2xl sm:text-3xl md:text-[34px] font-bold tracking-tighter leading-[1.08] text-foreground text-balance max-w-2xl">
              Learn from Experts. Operate with Confidence.
            </h3>
            <p className="mt-4 text-[18px] text-muted-foreground leading-relaxed text-pretty max-w-2xl">
              Rent Academy brings together industry leaders, experts, and operators to share trusted education, real-world
              strategies, and best practices.
            </p>

            <div className="mt-8">
              <p className="text-sm font-semibold text-foreground mb-4">Three Pillars of Education</p>
              <ul className="grid min-w-0 grid-cols-1 gap-x-10 lg:gap-x-16 gap-y-4">
                {PILLARS.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-base text-foreground">
                    <span
                      className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: BRAND_GREEN }}
                    >
                      <Check className="h-3 w-3 text-black" strokeWidth={3} />
                    </span>
                    <span className="leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right column — photo collage */}
          <div className="mt-10 lg:mt-6 shrink-0 flex justify-center lg:mr-12 xl:mr-24">
            <RentAcademyCollage />
          </div>
        </div>
      </div>
    </div>
  );
}
